import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import SystemAlertsFilter, { initialSystemAlertsFilterData } from "./SystemAlertsFilter";

const SystemAlerts = () => {
  const [config] = useAuth();
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);

  // Use string values for tabs (more reliable)
  const [activeTab, setActiveTab] = useState("exports");

  const [filters, setFilters] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialSystemAlertsFilterData;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(25);

  const [exportsData, setExportsData] = useState([]);
  const [loginFrequencyData, setLoginFrequencyData] = useState([]);
  const [moduleFrequencyData, setModuleFrequencyData] = useState([]);

  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });

  const [loginMeta, setLoginMeta] = useState({
    total_logins: 0,
    rolling_average: 0,
  });

  const [moduleMeta, setModuleMeta] = useState({
    total_visits: 0,
    rolling_average: 0,
  });

  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search || "");

  const handleError = (err) => {
    const message = err?.response?.data?.message;
    if (message && message !== "Too Many Attempts.") {
      NotificationManager.error(message);
    } else {
      NotificationManager.error("Failed to load system alerts.");
    }
  };

  const buildCommonParams = () => {
    const params = {
      active_tab: activeTab,        // ← Added here
      page: currentPage,
      per_page: itemPerPage,
    };

    if (filters.userId) params.user_id = filters.userId;
    if (filters.fromDate) params.from_date = filters.fromDate;
    if (filters.toDate) params.to_date = filters.toDate;
    if (filters.singleDate) {
      params.from_date = filters.singleDate;
      params.to_date = filters.singleDate;
    }
    if (debouncedSearch) params.search = debouncedSearch;

    return params;
  };

  const updatePagination = (pagination = {}, fallbackCount = 0) => {
    setPaginationData({
      totalItems: pagination?.total ?? fallbackCount,
      from: pagination?.from ?? 0,
      to: pagination?.to ?? 0,
      totalPages: pagination?.last_page ?? 0,
    });
  };

  const fetchExports = async (page = 1) => {
    const params = {
      ...buildCommonParams(),
      page,
    };
    if (filters.status) params.status = filters.status;

    const res = await axios.get(`${BASE_URL}/admin/system-alerts/exports`, {
      ...config,
      params,
    });

    const payload = res?.data?.data;
    const records = payload?.data || payload?.exports?.data || [];
    const pagination = payload?.pagination || payload?.meta || payload || {};

    setExportsData(records);
    updatePagination(pagination, records?.length || 0);
  };

  const fetchLoginFrequency = async (page = 1) => {
    const res = await axios.get(`${BASE_URL}/admin/system-alerts/login-frequency`, {
      ...config,
      params: {
        ...buildCommonParams(),
        page,
      },
    });

    const payload = res?.data?.data || {};
    setLoginFrequencyData(payload?.daily || []);
    updatePagination(payload?.pagination || {}, (payload?.daily || []).length);
    setLoginMeta({
      total_logins: payload?.rolling?.total_logins ?? payload?.total_logins ?? 0,
      rolling_average:
        payload?.rolling?.average_per_day ?? payload?.rolling_average ?? 0,
    });
  };

  const fetchModuleFrequency = async (page = 1) => {
    const params = {
      ...buildCommonParams(),
      page,
    };
    if (filters.module) params.module = filters.module;

    const res = await axios.get(`${BASE_URL}/admin/system-alerts/module-frequency`, {
      ...config,
      params,
    });

    const payload = res?.data?.data || {};
    setModuleFrequencyData(payload?.daily || []);
    updatePagination(payload?.pagination || {}, (payload?.daily || []).length);
    setModuleMeta({
      total_visits: payload?.rolling?.total_visits ?? payload?.total_visits ?? 0,
      rolling_average:
        payload?.rolling?.average_per_day ?? payload?.rolling_average ?? 0,
    });
  };

  const fetchData = async ({ page = 1 } = {}) => {
    setLoading(true);
    try {
      if (activeTab === "exports") {
        await fetchExports(page);
      } else if (activeTab === "login-frequency") {
        await fetchLoginFrequency(page);
      } else if (activeTab === "module-frequency") {
        await fetchModuleFrequency(page);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  // Update when tab or items per page changes
  useEffect(() => {
    setCurrentPage(1);
    fetchData({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, itemPerPage]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = (filters.search || "").trim();
      if (next.length === 0 || next.length >= 3) {
        setDebouncedSearch(next);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch when search changes
  useEffect(() => {
    setCurrentPage(1);
    fetchData({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (currentPage < paginationData.totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchData({ page: nextPage });
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      fetchData({ page: prevPage });
    }
  };

  const formatDateTime = (value) => {
    if (!value || value === "-") return "-";
    return moment(value).tz("America/New_York").format("MM/DD/YYYY hh:mm A");
  };

  const getValue = (row, keys = []) => {
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const value = key.includes(".")
        ? key.split(".").reduce((acc, part) => acc?.[part], row)
        : row?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return "-";
  };

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">System Alerts</p>
        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center">
            <SystemAlertsFilter
              filterData={filters}
              onSetFilterData={(value) => setFilters(value)}
              onCallApiAgain={(value) => {
                const targetTab = value?.tab && value?.tab !== "all" ? value.tab : activeTab;
                if (targetTab !== activeTab) {
                  setActiveTab(targetTab);
                } else {
                  setCurrentPage(1);
                  fetchData({ page: 1 });
                }
              }}
            />

            <div className="search-box contacts">
              <input
                type="text"
                className="body-S"
                placeholder="Search user, module..."
                value={filters.search || ""}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4">
          {[
            { key: "exports", label: "Export History" },
            { key: "login-frequency", label: "Login Frequency" },
            { key: "module-frequency", label: "Module Frequency" },
          ].map((tab) => (
            <p
              key={tab.key}
              role="button"
              className={`py-1 cursor-pointer ${activeTab === tab.key ? "head-4 dark-H active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </p>
          ))}
        </div>
      </div>

      <div>
        {loading ? (
          <Loader />
        ) : activeTab === "exports" ? (
          // ... your exports table (unchanged)
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {exportsData.length === 0 ? (
              <p className="body-N text-center py-4">No export events found.</p>
            ) : (
              <table className="contact-table two-rows-static light-bg-L overflow-x-auto">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th>Date/Time</th>
                    <th>User Name</th>
                    {/* <th>Type</th> */}
                    <th>Module</th>
                    <th>Format</th>
                    <th>Status</th>
                    <th>File Name</th>
                    <th>Record Count</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {exportsData.map((item, idx) => (
                    <tr key={item?.id || idx} className="dark-H body-N">
                      <td>{formatDateTime(getValue(item, ["created_at", "event_time", "date"]))}</td>
                      <td className="green-H capitalize">
                        {getValue(item, ["user_id", "user.id"]) !== "-" ? (
                          <Link to={`/user/${getValue(item, ["user_id", "user.id"])}`}>
                            {getValue(item, ["user_name", "user.name", "user.full_name"])}
                          </Link>
                        ) : (
                          getValue(item, ["user_name", "user.name", "user.full_name"])
                        )}
                      </td>
                      {/* <td>{getValue(item, ["type", "event_type"])}</td> */}
                      <td>{getValue(item, ["module", "module_name"])}</td>
                      <td>{getValue(item, ["format"])}</td>
                      <td className="capitalize">{getValue(item, ["status"])}</td>
                      <td>{getValue(item, ["file_name", "filename", "file"])}</td>
                      <td>{getValue(item, ["record_count"])}</td>
                      <td>{getValue(item, ["ip_address", "ip"])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : activeTab === "login-frequency" ? (
          // ... your login frequency table
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            <div className="overflow-x-auto">
              {loginFrequencyData.length === 0 ? (
                <p className="body-N text-center py-4">No login frequency data found.</p>
              ) : (
                <table className="contact-table two-rows-static light-bg-L">
                  <thead>
                    <tr className="uppercase body-N dark-M">
                      <th>Date/Time</th>
                      <th>User Name</th>
                      {/* <th>Module</th> */}
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginFrequencyData.map((item, idx) => (
                      <tr key={`${item?.date || idx}`} className="dark-H body-N">
                        <td>{formatDateTime(item?.date)}</td>
                        <td className="green-H capitalize">
                          {getValue(item, ["user_id", "user.id"]) !== "-" ? (
                            <Link to={`/user/${getValue(item, ["user_id", "user.id"])}`}>
                              {getValue(item, ["user_name", "user.name", "user.full_name"])}
                            </Link>
                          ) : (
                            getValue(item, ["user_name", "user.name", "user.full_name"])
                          )}
                        </td>
                        {/* <td>{getValue(item, ["module", "module_name"])}</td> */}
                        <td>{item?.count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          // Module Frequency Table
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            <div className="overflow-x-auto">
              {moduleFrequencyData.length === 0 ? (
                <p className="body-N text-center py-4">No module frequency data found.</p>
              ) : (
                <table className="contact-table two-rows-static light-bg-L">
                  <thead>
                    <tr className="uppercase body-N dark-M">
                      <th>Date/Time</th>
                      <th>User Name</th>
                      <th>Module</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleFrequencyData.map((item, idx) => (
                      <tr key={`${item?.date || idx}`} className="dark-H body-N">
                        <td>{formatDateTime(item?.date)}</td>
                        <td className="green-H capitalize">
                          {getValue(item, ["user_id", "user.id"]) !== "-" ? (
                            <Link to={`/user/${getValue(item, ["user_id", "user.id"])}`}>
                              {getValue(item, ["user_name", "user.name", "user.full_name"])}
                            </Link>
                          ) : (
                            getValue(item, ["user_name", "user.name", "user.full_name"])
                          )}
                        </td>
                        <td>{getValue(item, ["module", "module_name"])}</td>
                        <td>{item?.count ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Pagination */}
        {((activeTab === "exports" && exportsData.length > 0) ||
          (activeTab === "login-frequency" && loginFrequencyData.length > 0) ||
          (activeTab === "module-frequency" && moduleFrequencyData.length > 0)) && (
            <ContactPagination
              from="archive"
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={() => fetchData({ page: currentPage })}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value);
                setCurrentPage(1);
                fetchData({ page: 1 });
              }}
            />
          )}
      </div>
    </BaseLayout>
  );
};

export default SystemAlerts;